import React, { useCallback, useEffect, useState } from 'react';
import { Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import { erpAPI } from '../../services/api';
import SearchableSelect from '../../components/admin/SearchableSelect';
import Modal from '../../components/admin/Modal';
import Toast from '../../components/admin/Toast';
import { rows, errorText, Loading } from '../../utils/erpHelpers';
import './ERPManagement.css';

const REASON_TYPES = [
  { value: 'LOSS', label: 'Loss' },
  { value: 'GAIN', label: 'Gain' },
  { value: 'BOTH', label: 'Loss or Gain (by direction)' },
];

const ACCOUNT_FIELDS = [
  { key: 'inventory_asset_account', label: 'Inventory Asset Account', codeKey: 'inventory_asset_account_code', nameKey: 'inventory_asset_account_name', type: 'ASSET', hint: 'Default 1200' },
  { key: 'adjustment_expense_account', label: 'Inventory Variance Expense', codeKey: 'adjustment_expense_account_code', nameKey: 'adjustment_expense_account_name', type: 'EXPENSE', hint: 'Default 5300' },
  { key: 'adjustment_gain_account', label: 'Inventory Variance Gain', codeKey: 'adjustment_gain_account_code', nameKey: 'adjustment_gain_account_name', type: 'REVENUE', hint: 'Default 4200' },
  { key: 'supplier_receivable_account', label: 'Supplier Receivable', codeKey: 'supplier_receivable_account_code', nameKey: 'supplier_receivable_account_name', type: 'ASSET', hint: 'Default 1300' },
];

const InventorySettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [reasons, setReasons] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [settings, setSettings] = useState<any>(null);
  const [form, setForm] = useState<any>({ name: '', type: 'LOSS', requires_note: false, display_order: 0 });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [accountsOpen, setAccountsOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [reasonRes, acctRes, settingsRes] = await Promise.all([
        erpAPI.list('stock-adjustment-reasons'),
        erpAPI.list('accounts', { page_size: 500 }),
        erpAPI.list('inventory-settings', {}),
      ]);
      setReasons(rows(reasonRes.data));
      setAccounts(rows(acctRes.data));
      const data = settingsRes.data?.results || settingsRes.data;
      setSettings(Array.isArray(data) ? data[0] : data);
    } catch (error) { setMessage({ type: 'error', text: errorText(error) }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveReason = async () => {
    if (!form.name.trim()) { setMessage({ type: 'error', text: 'Reason name is required.' }); return; }
    setSaving(true);
    try {
      const payload = { ...form, name: form.name.trim() };
      if (editingId) {
        await erpAPI.update('stock-adjustment-reasons', editingId, payload);
        setMessage({ type: 'success', text: 'Reason updated.' });
      } else {
        await erpAPI.create('stock-adjustment-reasons', payload);
        setMessage({ type: 'success', text: 'Reason added.' });
      }
      setForm({ name: '', type: 'LOSS', requires_note: false, display_order: 0 });
      setEditingId(null);
      await load();
    } catch (error) { setMessage({ type: 'error', text: errorText(error) }); }
    finally { setSaving(false); }
  };

  const startEdit = (r: any) => {
    setEditingId(r.id);
    setForm({ name: r.name, type: r.type, requires_note: r.requires_note, is_active: r.is_active, display_order: r.display_order });
  };

  const deleteReason = async (r: any) => {
    if (!window.confirm(`Delete reason "${r.name}"?`)) return;
    try {
      await erpAPI.remove('stock-adjustment-reasons', r.id);
      setMessage({ type: 'success', text: 'Reason deleted.' });
      await load();
    } catch (error) { setMessage({ type: 'error', text: errorText(error) }); }
  };

  const saveSettings = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const payload: any = {};
      for (const f of ACCOUNT_FIELDS) payload[f.key] = settings[f.key];
      const res = await erpAPI.update('inventory-settings', settings.id, payload);
      setSettings(res.data);
      setAccountsOpen(false);
      setMessage({ type: 'success', text: 'Inventory posting accounts saved.' });
    } catch (error) { setMessage({ type: 'error', text: errorText(error) }); }
    finally { setSaving(false); }
  };

  const accountOpts = (type: string) =>
    accounts.filter(a => a.account_type === type && a.is_active !== false)
      .map(a => ({ value: a.id, label: `${a.code} — ${a.name}` }));

  const inputStyle: React.CSSProperties = {
    width: '100%', minHeight: 40, padding: '8px 12px', border: '1px solid #e2e8f0',
    borderRadius: 8, fontSize: 13, color: '#1e293b', background: '#fff',
    boxSizing: 'border-box', outline: 'none',
  };

  const label: React.CSSProperties = {
    display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b',
    textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6,
  };

  if (loading) return <Loading />;

  return (
    <div className="erp-page">
      <Toast show={Boolean(message)} message={message?.text || ''} type={message?.type || 'info'} onClose={() => setMessage(null)} />

      <div className="erp-heading" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', margin: 0 }}>Inventory Settings</h1>
          <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 14 }}>
            Configure stock adjustment reasons and the accounts used for inventory corrections and supplier returns.
          </p>
        </div>
        <button onClick={() => setAccountsOpen(true)} disabled={!settings}
          style={{
            display: 'flex', alignItems: 'center', gap: 7, border: 'none', cursor: settings ? 'pointer' : 'not-allowed',
            background: '#4f46e5', color: '#fff', padding: '10px 18px', borderRadius: 9, fontSize: 13, fontWeight: 700,
            opacity: settings ? 1 : 0.5, flexShrink: 0,
          }}>
          <Pencil size={14} /> Edit Accounts
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 20, alignItems: 'start' }}>
        {/* Reasons */}
        <div className="erp-card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', background: 'linear-gradient(135deg, #fef3c7, #fde68a)', borderBottom: '1px solid #fcd34d' }}>
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#92400e' }}>Stock Adjustment Reasons</h2>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#a16207' }}>
              Reasons map to accounting automatically — LOSS → adjustment expense, GAIN → adjustment gain, BOTH → by direction.
            </p>
          </div>
          <div style={{ padding: 18 }}>
            <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
              <div style={{ flex: 1 }}><label style={label}>Name</label>
                <input style={inputStyle} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Damaged Goods" /></div>
              <div style={{ flex: 1 }}><label style={label}>Type</label>
                <select style={inputStyle} value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                  {REASON_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select></div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
              <div style={{ flex: 1 }}><label style={label}>Display order</label>
                <input style={inputStyle} type="number" value={form.display_order || 0} onChange={e => setForm({ ...form, display_order: e.target.value })} /></div>
              <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', paddingBottom: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={Boolean(form.requires_note)} onChange={e => setForm({ ...form, requires_note: e.target.checked })} style={{ width: 15, height: 15, accentColor: '#f59e0b' }} />
                  Requires note
                </label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={saveReason} style={{
                display: 'flex', alignItems: 'center', gap: 6, border: 'none', cursor: 'pointer',
                background: '#f59e0b', color: '#fff', padding: '9px 16px', borderRadius: 9, fontSize: 13, fontWeight: 700,
              }}>
                {editingId ? <><Pencil size={14} /> Update Reason</> : <><Plus size={15} /> Add Reason</>}
              </button>
              {editingId && <button onClick={() => { setEditingId(null); setForm({ name: '', type: 'LOSS', requires_note: false, display_order: 0 }); }} className="btn-cancel">Cancel edit</button>}
            </div>

            <div className="erp-table-wrap" style={{ marginTop: 16 }}>
              <table>
                <thead><tr>
                  <th style={{ padding: '8px 10px', fontSize: 11 }}>Name</th>
                  <th style={{ padding: '8px 10px', fontSize: 11 }}>Type</th>
                  <th style={{ padding: '8px 10px', fontSize: 11 }}>Active</th>
                  <th style={{ padding: '8px 10px', fontSize: 11 }}>Note</th>
                  <th style={{ padding: '8px 10px', fontSize: 11, textAlign: 'center' }}></th>
                </tr></thead>
                <tbody>
                  {reasons.map(r => (
                    <tr key={r.id}>
                      <td style={{ padding: '8px 10px', fontSize: 12, fontWeight: 600 }}>{r.name}</td>
                      <td style={{ padding: '8px 10px', fontSize: 12 }}>
                        <span style={{
                          display: 'inline-flex', padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 700,
                          background: r.type === 'GAIN' ? '#dcfce7' : r.type === 'LOSS' ? '#fee2e2' : '#e0e7ff',
                          color: r.type === 'GAIN' ? '#15803d' : r.type === 'LOSS' ? '#b91c1c' : '#4338ca',
                        }}>
                          {REASON_TYPES.find(t => t.value === r.type)?.label || r.type}
                        </span>
                      </td>
                      <td style={{ padding: '8px 10px', fontSize: 12 }}>{r.is_active ? 'Yes' : 'No'}</td>
                      <td style={{ padding: '8px 10px', fontSize: 12 }}>{r.requires_note ? 'Required' : 'Optional'}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                        <button onClick={() => startEdit(r)} style={{ border: 0, background: '#f1f5f9', borderRadius: 7, width: 28, height: 28, marginRight: 6, cursor: 'pointer', color: '#334155', display: 'inline-grid', placeItems: 'center' }} title="Edit"><Pencil size={13} /></button>
                        <button onClick={() => deleteReason(r)} style={{ border: 0, background: '#fef2f2', borderRadius: 7, width: 28, height: 28, cursor: 'pointer', color: '#dc2626', display: 'inline-grid', placeItems: 'center' }} title="Delete"><Trash2 size={13} /></button>
                      </td>
                    </tr>
                  ))}
                  {reasons.length === 0 && <tr><td colSpan={5} className="erp-empty">No reasons configured.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <Modal isOpen={accountsOpen} onClose={() => setAccountsOpen(false)} title="Inventory Posting Accounts" size="large">
        <p className="erp-modal-intro">
          Every stock adjustment and supplier return posts to these accounts. Account types are validated.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {ACCOUNT_FIELDS.map(f => (
            <div key={f.key}>
              <label style={label}>{f.label} <span style={{ fontWeight: 400, textTransform: 'none', color: '#94a3b8' }}>{f.hint}</span></label>
              <SearchableSelect
                value={settings?.[f.key] || ''}
                onChange={next => setSettings((s: any) => ({ ...s, [f.key]: next }))}
                placeholder={`Select a ${f.type} account`}
                options={accountOpts(f.type)} />
              <div style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 4 }}>
                Must be {f.type} · current: {settings?.[f.codeKey] || '—'} {settings?.[f.nameKey] ? `(${settings[f.nameKey]})` : ''}
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 12, padding: 10, background: '#f8fafc', borderRadius: 8, fontSize: 11.5, color: '#64748b', lineHeight: 1.6 }}>
          <strong style={{ color: '#334155' }}>Rule:</strong> a cash/bank account is only required when actual money moves. Stock adjustments, credit purchases and purchase returns never need one.
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 18 }}>
          <button className="btn-cancel" onClick={() => setAccountsOpen(false)}>Cancel</button>
          <button onClick={saveSettings} disabled={!settings}
            style={{
              display: 'flex', alignItems: 'center', gap: 7, border: 'none', cursor: settings ? 'pointer' : 'not-allowed',
              background: '#4f46e5', color: '#fff', padding: '10px 18px', borderRadius: 9, fontSize: 13, fontWeight: 700,
              opacity: settings ? 1 : 0.5,
            }}>
            <Save size={15} /> Save Accounts
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default InventorySettings;